import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from './lib/supabase';

export default function App() {
  return (
    <>
      <ScrollToTop />
      <div className="landing-layout">
        <Header />
        <main className="landing-main">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
  return (
    <header className="landing-header">
      <Link to="/" className="landing-logo-link">
        <img src="/logo.png" alt="Isopepe" className="landing-logo-img" />
      </Link>
      <div className="header-right">
        <div className="badge-beta">Private Beta</div>
        <a href="https://app.isopepe.com" className="btn-header-cta">
          Open App
        </a>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Home Page
// ---------------------------------------------------------------------------

function HomePage() {
  return (
    <>
      {/* Hero */}
      <section className="hero-section">
        <h1 className="hero-title">
          Your body generates data.<br />
          <span className="hero-title-accent">We turn it into decisions.</span>
        </h1>
        <p className="hero-description">
          Isopepe is an AI-native training platform for hybrid athletes.
          It connects your wearables, parses your workouts, models your physiology,
          and tells you exactly what to do today &mdash; whether that's a
          threshold interval, a heavy deadlift, or a recovery walk.
        </p>
        <WaitlistForm />
        <div className="hero-status">
          <span className="status-dot" />
          Currently in Private Beta &middot; Access by invitation
        </div>
        <a href="https://app.isopepe.com" className="hero-login-link">
          Already have access? Open App &rarr;
        </a>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="features-grid">
          <FeatureCard
            icon="brain"
            title="AI-Native Coaching"
            description="An MCP-powered AI assistant that understands your training history, physiology, and goals. Ask it anything about your next session."
          />
          <FeatureCard
            icon="chart"
            title="Deep Analytics"
            description="Performance Management Chart, HRV trends with SWC bands, acute-to-chronic workload ratio, and power/pace duration curves."
          />
          <FeatureCard
            icon="heart"
            title="Physiological Readiness"
            description="Oura Ring and Wahoo integration. Your resting heart rate, HRV baseline, sleep quality, and temperature deviation — distilled into one signal."
          />
          <FeatureCard
            icon="dumbbell"
            title="Hybrid Training"
            description="Strength and endurance in one platform. Structured intervals, progressive overload, block periodization — all from your phone."
          />
          <FeatureCard
            icon="upload"
            title="FIT File Intelligence"
            description="Drop a .FIT file and get instant analysis: normalized power, heart rate zones, efficiency factor, training stress — no manual entry."
          />
          <FeatureCard
            icon="shield"
            title="Privacy First"
            description="EU-hosted infrastructure. GDPR compliant. Your biometric and training data is yours — we never sell it."
          />
        </div>
      </section>

      {/* How it works */}
      <section className="how-section">
        <h2 className="section-title">How it works</h2>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3 className="step-title">Connect</h3>
            <p className="step-description">
              Link your Oura Ring, Wahoo, or upload FIT files from Garmin, Polar, or any device.
            </p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3 className="step-title">Train</h3>
            <p className="step-description">
              Build workouts with the structured editor. Track strength, cardio, and hybrid sessions.
            </p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3 className="step-title">Adapt</h3>
            <p className="step-description">
              The platform learns your patterns. It tells you when to push, when to recover, and what to change.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="cta-section">
        <h2 className="cta-title">Ready to train smarter?</h2>
        <WaitlistForm />
      </section>
    </>
  );
}

function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    if (!supabase) { setStatus('error'); return; }
    const { error } = await supabase.rpc('join_waitlist', { p_email: email.trim() });
    setStatus(error ? 'error' : 'success');
  }

  if (status === 'success') {
    return (
      <div className="waitlist-form">
        <p className="waitlist-success">You're on the list! We'll be in touch.</p>
      </div>
    );
  }

  return (
    <form className="waitlist-form" onSubmit={handleSubmit}>
      <div className="waitlist-input-group">
        <input
          type="email"
          className="waitlist-input"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={status === 'loading'}
        />
        <button type="submit" className="btn-primary btn-lg" disabled={status === 'loading'}>
          {status === 'loading' ? 'Joining...' : 'Join Waitlist'}
        </button>
      </div>
      {status === 'error' && (
        <p className="waitlist-error">Something went wrong. Please try again.</p>
      )}
    </form>
  );
}

const ICONS: Record<string, string> = {
  brain: 'M12 2a7 7 0 0 0-4.6 12.3A4 4 0 0 0 8 18v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2a4 4 0 0 0 .6-3.7A7 7 0 0 0 12 2Z',
  chart: 'M3 3v18h18M7 16l4-6 4 4 4-8',
  heart: 'M12 21C12 21 4 14 4 8.5C4 5.4 6.4 3 9.5 3c1.7 0 3.4.8 4.5 2.1C15.1 3.8 16.8 3 18.5 3C21.6 3 24 5.4 24 8.5C24 14 12 21 12 21z',
  dumbbell: 'M6.5 6.5h11M17 4v5M7 4v5M17 15v5M7 15v5M4 9h16v6H4z',
  upload: 'M12 3v12M5 10l7-7 7 7M5 21h14',
  shield: 'M12 2l8 4v6c0 5.5-3.8 10.7-8 12-4.2-1.3-8-6.5-8-12V6l8-4z',
};

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="feature-card">
      <div className="feature-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d={ICONS[icon] ?? ICONS.chart} />
        </svg>
      </div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-description">{description}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Terms of Service
// ---------------------------------------------------------------------------

function TermsPage() {
  return (
    <article className="legal-doc">
      <h1>Terms of Service</h1>
      <p className="legal-subtitle">Käyttöehdot</p>
      <p className="last-updated">Last updated: April 2026</p>

      <h2>1. Agreement to Terms</h2>
      <p>
        Welcome to Isopepe. By accessing or using our services at{' '}
        <code>app.isopepe.com</code>, you agree to be bound by these Terms of Service
        ("Terms"). If you do not agree to these Terms, do not use the service.
        Isopepe is currently operating in a <strong>Private Beta</strong> phase.
      </p>

      <h2>2. Description of Service</h2>
      <p>
        Isopepe is a workout tracking, training analytics, and physiological readiness
        platform. The service processes biometric data from wearable devices (such as
        Oura Ring and Wahoo), workout files (FIT format), and user-entered wellness
        data to provide training insights, readiness assessments, and performance
        analytics.
      </p>

      <h2>3. Health and Wellness Disclaimer</h2>
      <p>
        <strong>Isopepe is not a medical device, diagnostic tool, or substitute for
        professional medical advice, diagnosis, or treatment.</strong> The readiness
        scores, training recommendations, HRV analysis, and any other outputs provided
        by the service are for informational and educational purposes only.
      </p>
      <p>
        You acknowledge and agree that:
      </p>
      <ul>
        <li>You are solely responsible for your own health, safety, and training decisions.</li>
        <li>You should always consult a qualified healthcare professional before beginning,
        modifying, or continuing any exercise program, especially if you have any
        pre-existing medical conditions.</li>
        <li>Biometric data processed by Isopepe (including heart rate variability,
        resting heart rate, sleep metrics, and temperature data) may be inaccurate
        due to device limitations and should not be relied upon for medical decisions.</li>
        <li>Isopepe and its creators accept no liability for any injuries, health
        complications, adverse effects, or damages of any kind arising from the use
        of the service, reliance on its outputs, or following any training suggestions
        displayed within the platform.</li>
      </ul>

      <h2>4. User Accounts and Data</h2>
      <p>
        During the Private Beta, access is by invitation only. You are responsible for
        maintaining the confidentiality of your login credentials. You retain all rights
        to your personal training and biometric data. You grant Isopepe a limited,
        non-exclusive license to process your data solely for the purpose of providing
        the service.
      </p>

      <h2>5. Acceptable Use</h2>
      <p>
        You agree not to: (a) use the service for any unlawful purpose; (b) attempt to
        gain unauthorized access to any part of the service; (c) interfere with or
        disrupt the service; (d) upload malicious files or content; (e) use automated
        tools to scrape or collect data from the service.
      </p>

      <h2>6. Beta Service Disclaimer</h2>
      <p>
        The service is provided <strong>"as is"</strong> and <strong>"as available"</strong>{' '}
        without warranties of any kind, express or implied. As a Private Beta service,
        we reserve the right to modify, suspend, or terminate the service at any time
        without prior notice. Such changes may result in the loss of data, features,
        or access. We make no guarantees regarding uptime, data persistence, or
        continued availability.
      </p>

      <h2>7. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by Finnish law, Isopepe and its creators,
        employees, and affiliates shall not be liable for any indirect, incidental,
        special, consequential, or punitive damages, including but not limited to loss
        of data, loss of profits, personal injury, or health-related damages arising
        from the use of or inability to use the service. Our total aggregate liability
        shall not exceed the amount you have paid to use the service in the 12 months
        preceding the claim, or EUR 100, whichever is greater.
      </p>

      <h2>8. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Isopepe and its creators from any
        claims, damages, losses, or expenses (including reasonable legal fees) arising
        from your use of the service, your violation of these Terms, or your violation
        of any rights of another party.
      </p>

      <h2>9. Changes to Terms</h2>
      <p>
        We may update these Terms as the service evolves. Material changes will be
        communicated to registered users via email or in-app notification. Continued
        use of the service after such changes constitutes acceptance of the updated Terms.
      </p>

      <h2>10. Governing Law and Dispute Resolution</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of
        Finland, without regard to conflict of law provisions. Any disputes arising out
        of or in connection with these Terms shall be resolved in the district court of
        Helsinki, Finland. Nothing in these Terms shall limit your rights as a consumer
        under mandatory Finnish or EU consumer protection legislation.
      </p>

      <h2>11. Contact</h2>
      <p>
        For questions about these Terms, contact us at{' '}
        <a href="mailto:legal@isopepe.com">legal@isopepe.com</a>.
      </p>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Privacy Policy
// ---------------------------------------------------------------------------

function PrivacyPage() {
  return (
    <article className="legal-doc">
      <h1>Privacy Policy</h1>
      <p className="legal-subtitle">Tietosuojaseloste</p>
      <p className="last-updated">Last updated: April 2026</p>
      <p className="legal-intro">
        This Privacy Policy explains how Isopepe collects, uses, stores, and protects
        your personal data, including special categories of data (health and biometric
        data), in compliance with the EU General Data Protection Regulation (GDPR) and
        the Finnish Data Protection Act (Tietosuojalaki 1050/2018).
      </p>

      <h2>1. Data Controller</h2>
      <p>
        Isopepe ("we", "us", "our") acts as the data controller for the personal data
        processed within the service.
      </p>
      <p>
        Contact: <a href="mailto:privacy@isopepe.com">privacy@isopepe.com</a>
      </p>

      <h2>2. Categories of Personal Data</h2>
      <p>We collect and process the following categories of data:</p>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Data</th>
            <th>GDPR Classification</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Account</td>
            <td>Email address, username, profile picture</td>
            <td>Personal data</td>
          </tr>
          <tr>
            <td>Profile</td>
            <td>Age, weight, height, biological sex</td>
            <td>Personal data</td>
          </tr>
          <tr>
            <td>Biometric &amp; Health</td>
            <td>Heart rate (resting, max, zones), heart rate variability (RMSSD),
              blood oxygen (SpO2), body temperature deviation, sleep stages and duration,
              respiratory rate, readiness/recovery scores</td>
            <td><strong>Special category data (Art. 9)</strong></td>
          </tr>
          <tr>
            <td>Training</td>
            <td>Workout structures, FIT/TCX files, GPS tracks, sensor streams
              (power, cadence, pace), training load metrics (TSS, CTL, ATL, TSB)</td>
            <td>Personal data</td>
          </tr>
          <tr>
            <td>Integration</td>
            <td>Data from authorized third-party services (Oura Ring, Wahoo, Garmin)</td>
            <td>Personal data / Special category</td>
          </tr>
          <tr>
            <td>Social</td>
            <td>Activity feed posts, kudos, comments, follow relationships</td>
            <td>Personal data</td>
          </tr>
          <tr>
            <td>Technical</td>
            <td>IP address, browser type, device info, error logs</td>
            <td>Personal data</td>
          </tr>
        </tbody>
      </table>

      <h2>3. Purpose and Legal Basis for Processing</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Purpose</th>
            <th>Legal Basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Providing the training analytics service</td>
            <td>Performance of contract (Art. 6(1)(b))</td>
          </tr>
          <tr>
            <td>Processing health and biometric data</td>
            <td>Explicit consent (Art. 9(2)(a))</td>
          </tr>
          <tr>
            <td>Computing readiness, HRV baselines, and training recommendations</td>
            <td>Explicit consent (Art. 9(2)(a))</td>
          </tr>
          <tr>
            <td>Service improvement and error monitoring</td>
            <td>Legitimate interest (Art. 6(1)(f))</td>
          </tr>
          <tr>
            <td>Communicating service changes</td>
            <td>Legitimate interest (Art. 6(1)(f))</td>
          </tr>
        </tbody>
      </table>
      <p>
        You provide explicit consent for health data processing when you: (a) connect
        a wearable device, (b) upload workout files containing biometric data, or
        (c) manually enter wellness check-in data. You may withdraw consent at any time
        by disconnecting integrations and requesting data deletion.
      </p>

      <h2>4. Data Storage and Security</h2>
      <ul>
        <li><strong>Location:</strong> All data is stored within the European Union
          using Supabase infrastructure (AWS eu-central-1, Frankfurt, Germany).</li>
        <li><strong>Encryption:</strong> Data is encrypted in transit (TLS 1.2+) and
          at rest (AES-256).</li>
        <li><strong>Access control:</strong> Row-level security (RLS) policies ensure
          users can only access their own data.</li>
        <li><strong>Backups:</strong> Automated daily backups with point-in-time recovery.</li>
        <li><strong>Retention:</strong> Data is retained for as long as your account is active.
          Upon account deletion, all personal data is permanently removed within 30 days.</li>
      </ul>

      <h2>5. Data Sharing</h2>
      <p>We <strong>do not sell</strong> your personal or biometric data to third parties. Data may be shared with:</p>
      <ul>
        <li><strong>Sub-processors:</strong> Hosting providers (Supabase/AWS), error monitoring
          (Sentry), and CDN (Cloudflare) — strictly for providing the service.</li>
        <li><strong>Third-party integrations:</strong> When you explicitly connect a service
          (Oura, Wahoo), data flows according to that service's privacy policy.</li>
        <li><strong>Other users:</strong> If your profile is public, your username, profile
          picture, and workout summaries may be visible to other platform users. Detailed
          biometric data (HRV, sleep, heart rate) is never shared.</li>
        <li><strong>Legal obligations:</strong> We may disclose data when required by Finnish
          or EU law, or in response to valid legal process.</li>
      </ul>

      <h2>6. International Transfers</h2>
      <p>
        Your data is processed and stored exclusively within the EU/EEA. In the event
        that any sub-processor operates outside the EU/EEA, we ensure appropriate
        safeguards are in place (Standard Contractual Clauses or adequacy decisions)
        in accordance with GDPR Chapter V.
      </p>

      <h2>7. Your Rights</h2>
      <p>Under the GDPR, you have the right to:</p>
      <ul>
        <li><strong>Access</strong> the personal data we hold about you (Art. 15).</li>
        <li><strong>Rectification</strong> of inaccurate data (Art. 16).</li>
        <li><strong>Erasure</strong> of your data — "right to be forgotten" (Art. 17).</li>
        <li><strong>Restriction</strong> of processing (Art. 18).</li>
        <li><strong>Data portability</strong> — receive your data in a structured,
          machine-readable format (Art. 20).</li>
        <li><strong>Object</strong> to processing based on legitimate interest (Art. 21).</li>
        <li><strong>Withdraw consent</strong> at any time without affecting the lawfulness
          of prior processing (Art. 7(3)).</li>
        <li><strong>Lodge a complaint</strong> with the Finnish Data Protection Ombudsman
          (Tietosuojavaltuutetun toimisto) at{' '}
          <a href="https://tietosuoja.fi/en" target="_blank" rel="noopener noreferrer">
            tietosuoja.fi
          </a>.</li>
      </ul>
      <p>
        To exercise any of these rights, contact us at{' '}
        <a href="mailto:privacy@isopepe.com">privacy@isopepe.com</a> or use the
        account management tools in the app.
      </p>

      <h2>8. Automated Decision-Making</h2>
      <p>
        Isopepe uses automated processing to compute readiness scores, training
        recommendations, and HRV-based recovery assessments. These outputs are
        informational only and do not constitute legally or medically significant
        decisions. You are always free to disregard any automated recommendation.
      </p>

      <h2>9. Children's Privacy</h2>
      <p>
        Isopepe is not intended for use by individuals under 16 years of age. We do
        not knowingly collect personal data from children. If you believe a child has
        provided us with personal data, please contact us for deletion.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this policy as the service evolves. Material changes will be
        communicated to registered users via email at least 14 days before taking effect.
        The "Last updated" date at the top of this page reflects the most recent revision.
      </p>

      <h2>11. Contact</h2>
      <p>
        Data Protection Contact: <a href="mailto:privacy@isopepe.com">privacy@isopepe.com</a>
      </p>
      <p>
        Finnish Data Protection Ombudsman:{' '}
        <a href="https://tietosuoja.fi/en" target="_blank" rel="noopener noreferrer">
          tietosuoja.fi
        </a>
      </p>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function Footer() {
  return (
    <footer className="landing-footer">
      <div className="footer-links">
        <Link to="/terms">Terms of Service</Link>
        <span className="footer-dot">&middot;</span>
        <Link to="/privacy">Privacy Policy</Link>
        <span className="footer-dot">&middot;</span>
        <a href="mailto:hello@isopepe.com">Contact</a>
      </div>
      <div className="footer-copyright">
        &copy; {new Date().getFullYear()} Isopepe. Helsinki, Finland.
      </div>
    </footer>
  );
}
